/**
 * actionContext — qué contexto enseña cada tipo de acción.
 *
 * Puro y sin React: entra un `action_item` de GET /actions, salen las filas
 * que la tarjeta pinta. Una sola función decide esto para que un pago y una
 * tarea no acaben describiéndose con vocabularios distintos.
 *
 * La regla que gobierna todo el fichero: **solo se enseña lo que el item ya
 * trae**. No se llama a nadie, no se deduce y no se rellena un hueco con algo
 * plausible.
 *
 * Qué hacer con un hueco depende de si el dato estaba prometido:
 *
 *   - En un SEGUIMIENTO la lista de filas es fija y el hueco se enseña ("—").
 *     Que no sepamos cuándo se habló por última vez con un lead es justo lo
 *     que un comercial necesita ver.
 *   - En el resto son metadatos genéricos, no campos prometidos: "Responsable:
 *     —" en un pago no le dice nada a nadie, así que la fila se omite.
 *
 * Dos cosas que NO se enseñan, a propósito:
 *
 *   `meta.amount_impact` es un número suelto, sin moneda (alerts_engine lo
 *   emite como `float(p["amount"])`). Pintarlo obligaría a elegir un símbolo,
 *   y una cifra con la moneda equivocada es peor que ninguna cifra. El
 *   importe con su moneda ya viaja en la descripción del item.
 *
 *   `meta.villa_id` es un identificador, no un nombre. "villa_3" no le dice
 *   nada a nadie, y la descripción ya nombra la villa.
 */
import { humaniseActionType, lastInteractionLabel, relativeLabel }
  from "@/lib/followUpVocabulary";
import { statusLabel } from "@/lib/leadStatuses";
import { automationReasonText } from "@/lib/automationApi";

const PRIORITY_LABEL = { alta: "Alta", media: "Media", baja: "Baja" };
const SCOPE_LABEL = {
  admin: "Administración", marketing: "Comercial",
  constructor: "Obra", investor: "Inversores",
};

/** Una fila. El valor puede ser null; quién sobrevive lo decide cada caso. */
const row = (key, label, value) => ({ key, label, value: value || null });

/**
 * "hace 12 días" a partir del contador que ya calcula el backend.
 *
 * 999 no es una antigüedad: `alerts_engine` lo usa como centinela de "esto no
 * ha pasado NUNCA" (una villa sin un solo avance publicado). Pintar "hace 999
 * días" sería inventarse una fecha que no existe — y la descripción de la
 * alerta ya dice que nunca ocurrió, así que aquí no se enseña nada.
 */
function ageLabel(days) {
  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0 || n >= 999) return null;
  return n === 1 ? "hace 1 día" : `hace ${Math.round(n)} días`;
}

/** Las filas de contexto de una acción, en orden de lectura. */
export function contextRowsFor(item, now = new Date()) {
  if (!item) return [];
  const meta = item.meta || {};
  const owner = item.owner?.name || null;

  // Un seguimiento de verdad se reconoce por la marca del motor, NO por
  // `type`: las alertas residuales de lead (`lead.needs_response` y
  // compañía) también llegan con type "follow_up" y no traen nada del
  // detalle comercial. Preguntar por el tipo pintaba seis filas vacías.
  if (meta.followup_kind) {
    // Lista FIJA: aquí un hueco es información. Que no sepamos cuándo se
    // habló por última vez con un lead es justo lo que hay que ver.
    return [
      row("lead", "Lead", meta.lead_name),
      row("stage", "Estado", meta.stage ? statusLabel(meta.stage) || meta.stage : null),
      row("due", "Cuándo toca", relativeLabel(meta.next_action_at, now)),
      row("last", "Últ. interacción",
          lastInteractionLabel(meta.last_message_sent_at, meta.last_message_channel)),
      row("next", "Próxima acción",
          meta.next_action_type ? humaniseActionType(meta.next_action_type) : null),
      row("owner", "Responsable", meta.assigned_to_name),
    ];
  }

  // El resto: solo lo que se sabe. Estas filas son metadatos genéricos, no un
  // conjunto de campos prometido, así que una fila vacía no dice nada y solo
  // alarga la tarjeta — se omite en vez de enseñar "—".
  const known = (rows) => rows.filter((r) => r && r.value);

  switch (item.type) {
    case "task":
      return known([
        row("priority", "Prioridad", PRIORITY_LABEL[meta.priority] || meta.priority),
        row("owner", "Responsable", meta.assignee || owner),
        row("scope", "Área", SCOPE_LABEL[meta.scope] || meta.scope),
        row("due", "Vence", relativeLabel(item.due_at, now)),
        meta.is_blocker ? { key: "blocker", label: "Bloquea", value: "Sí" } : null,
      ]);

    case "automation":
      return known([
        row("step", "Paso", meta.step ? `Paso ${meta.step}` : null),
        row("reason", "Motivo", automationReasonText(meta.reason)),
        row("due", "Detenida", relativeLabel(item.due_at, now)),
      ]);

    // payment / construction / document / y cualquier alerta futura: el
    // adaptador de alertas emite siempre la misma forma.
    default:
      return known([
        row("due", "Vence", relativeLabel(item.due_at, now)),
        row("age", "Antigüedad", ageLabel(meta.days_old)),
        row("owner", "Responsable", owner),
      ]);
  }
}

/**
 * Mutaciones que se enseñan como botón en vez de esconderse en el menú.
 *
 * El criterio es el de Seguimientos: lo que resuelve la tarjeta va a la
 * vista; lo que la hace desaparecer sin resolverla (descartar, ocultar) se
 * queda un paso más lejos, en el menú, donde no se pulsa por inercia.
 */
const PROMOTED = new Set([
  "followup_complete", "followup_snooze", "task_complete",
  "reply_acknowledge", "alert_snooze",
]);

export function promotedSecondaries(item) {
  const list = Array.isArray(item?.cta_secondary) ? item.cta_secondary : [];
  return list.filter((c) => PROMOTED.has(c.action_key));
}

export function menuSecondaries(item) {
  const list = Array.isArray(item?.cta_secondary) ? item.cta_secondary : [];
  return list.filter((c) => !PROMOTED.has(c.action_key));
}
